import { useCallback, useEffect, useState } from 'react';
import config from '../utils/config';

const UPDATE_CHECK_INTERVAL_MS = 60 * 60 * 1000;

let reloading = false;
const reloadOnce = () => {
    if (reloading) {
        return;
    }
    reloading = true;
    window.location.reload();
};

const isServiceWorkerEnabled = () => process.env.NODE_ENV === 'production'
    && !config.useEmulators
    // The native app ships its files in the app bundle, a service worker would only serve stale copies
    && !config.isNativeApp
    && 'serviceWorker' in navigator;

// Registers the service worker and reports when a new deployed version is downloaded and waiting.
// Installed apps can stay open for days, so we also check for updates hourly and whenever the app comes back to the foreground.
const useServiceWorkerUpdate = () => {
    const [waitingWorker, setWaitingWorker] = useState<ServiceWorker | null>(null);

    useEffect(() => {
        if (!isServiceWorkerEnabled()) {
            return;
        }

        // On the very first install there is no previous controller, so nothing to reload.
        // Tracked over time: the first install takes control silently, and later updates must still reload.
        let currentController = navigator.serviceWorker.controller;
        let registration: ServiceWorkerRegistration | undefined;

        const trackInstalling = (worker: ServiceWorker) => {
            worker.addEventListener('statechange', () => {
                if (worker.state === 'installed' && navigator.serviceWorker.controller) {
                    setWaitingWorker(worker);
                }
            });
        };

        navigator.serviceWorker.register(`${process.env.PUBLIC_URL}/service-worker.js`)
            .then(reg => {
                registration = reg;
                if (reg.waiting && navigator.serviceWorker.controller) {
                    setWaitingWorker(reg.waiting);
                }
                if (reg.installing) {
                    trackInstalling(reg.installing);
                }
                reg.addEventListener('updatefound', () => {
                    if (reg.installing) {
                        trackInstalling(reg.installing);
                    }
                });
            })
            .catch(e => console.error('Service worker registration failed', e));

        const checkForUpdate = () => {
            // Fails when offline, which is fine - we'll try again later
            registration?.update().catch(() => { });
        };
        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
                checkForUpdate();
            }
        };
        const interval = setInterval(checkForUpdate, UPDATE_CHECK_INTERVAL_MS);
        document.addEventListener('visibilitychange', handleVisibilityChange);

        // The new version took over (in this tab or another one) - reload so the page runs the matching code
        const handleControllerChange = () => {
            const previousController = currentController;
            currentController = navigator.serviceWorker.controller;
            if (previousController) {
                reloadOnce();
            }
        };
        navigator.serviceWorker.addEventListener('controllerchange', handleControllerChange);

        return () => {
            clearInterval(interval);
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            navigator.serviceWorker.removeEventListener('controllerchange', handleControllerChange);
        };
    }, []);

    const applyUpdate = useCallback(() => {
        if (!waitingWorker) {
            return;
        }
        // Fallback in case `controllerchange` doesn't fire in this tab
        waitingWorker.addEventListener('statechange', () => {
            if (waitingWorker.state === 'activated') {
                reloadOnce();
            }
        });
        waitingWorker.postMessage({ type: 'SKIP_WAITING' });
    }, [waitingWorker]);

    return { updateAvailable: !!waitingWorker, applyUpdate };
};

export default useServiceWorkerUpdate;
